import React, { useState, useCallback } from "react";
import { Sparkles, RefreshCw, CheckCircle2, Copy, Check } from "lucide-react";

const STEPS = [
    { key: "loading", label: "Loading league data…" },
    { key: "context", label: "Building context…" },
    { key: "ai", label: "Calling AI…" },
    { key: "format", label: "Finalizing…" },
];

// Maps the SSE step label → step key for progress tracking
const LABEL_TO_KEY = {
    "Loading league data…": "loading",
    "Building context…": "context",
    "Calling AI…": "ai",
    "Finalizing…": "format",
};

const REPORT_SECTIONS = [
    { key: "awards", emoji: "🏆", label: "Awards" },
    { key: "power_rankings", emoji: "📈", label: "Power Rankings" },
    { key: "luck", emoji: "🍀", label: "Luck Report" },
    { key: "standings", emoji: "📊", label: "Standings" },
];

/**
 * WeeklyNarrative — on-demand LLM commentary for the weekly report.
 *
 * POSTs to /api/weekly-report/.../stream/ (SSE endpoint) and displays
 * progressive step feedback while the LLM is running.
 */
const WeeklyNarrative = ({ provider, leagueYear, leagueId, week }) => {
    const [sections, setSections] = useState(null);
    const [formattedOutput, setFormattedOutput] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [weeklyContext, setWeeklyContext] = useState("");
    const [currentStep, setCurrentStep] = useState(null);
    const [doneSteps, setDoneSteps] = useState([]);
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(() => {
        if (!formattedOutput) return;
        navigator.clipboard.writeText(formattedOutput).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [formattedOutput]);

    const generate = useCallback(
        async (regenerate = false) => {
            setLoading(true);
            setError(null);
            // Prime the first step so its spinner shows immediately (Bug fix #2)
            setCurrentStep("loading");
            setDoneSteps([]);

            try {
                const token = localStorage.getItem("doritostatsToken");
                const response = await fetch(
                    `/api/weekly-report/${provider}/${leagueYear}/${leagueId}/${week}/stream/`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            ...(token ? { Authorization: `Token ${token}` } : {}),
                        },
                        body: JSON.stringify({
                            weekly_context: weeklyContext,
                            tone: "snarky",
                            regenerate,
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    // SSE events are separated by double newlines
                    const parts = buffer.split("\n\n");
                    buffer = parts.pop(); // hold incomplete trailing chunk

                    for (const part of parts) {
                        const line = part.trim();
                        if (!line.startsWith("data: ")) continue;
                        let data;
                        try {
                            data = JSON.parse(line.slice(6));
                        } catch {
                            continue;
                        }

                        if (data.type === "step") {
                            const key = LABEL_TO_KEY[data.label];
                            if (key) {
                                // mark previous step done, activate new step
                                setCurrentStep((prev) => {
                                    if (prev) setDoneSteps((d) => [...d, prev]);
                                    return key;
                                });
                            }
                        } else if (data.type === "done") {
                            setDoneSteps(STEPS.map((s) => s.key));
                            setSections(data.sections ?? null);
                            setFormattedOutput(data.formatted_output ?? "");
                            if (data.weekly_context != null) {
                                setWeeklyContext(data.weekly_context);
                            }
                            return;
                        } else if (data.type === "error") {
                            setError(data.message || "Failed to generate report. Please try again.");
                            return;
                        }
                    }
                }
            } catch (err) {
                setError("Failed to generate report. Please try again.");
            } finally {
                setLoading(false);
                setCurrentStep(null);
            }
        },
        [provider, leagueYear, leagueId, week, weeklyContext]
    );

    return (
        <div className="mb-6 border border-border-card rounded-xl bg-surface overflow-hidden">
            {/* Header row */}
            <div className="flex items-center px-5 py-4 border-b border-border-card">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-mustard" />
                    <span className="font-sans font-bold text-ds-text text-sm">
                        Weekly Narrative
                    </span>
                    <span className="text-xs text-text-muted font-sans">
                        — AI-generated commentary
                    </span>
                </div>
            </div>

            {/* Commissioner notes — always visible */}
            <div className="px-5 py-3 border-b border-border-card bg-bg">
                <label className="block text-xs font-sans font-semibold text-text-muted mb-1.5 uppercase tracking-wide">
                    Commissioner notes (optional)
                </label>
                <textarea
                    value={weeklyContext}
                    onChange={(e) => setWeeklyContext(e.target.value)}
                    placeholder="e.g. It's rivalry week. Isabella just traded Patrick Mahomes to Gianna. Marco just got back from vacation."
                    rows={2}
                    className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-surface text-ds-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-mustard resize-none"
                />
            </div>

            {/* Body */}
            <div className="px-5 py-4">
                {/* Not yet generated */}
                {!sections && !loading && !error && (
                    <div className="flex flex-col items-start gap-3">
                        <p className="text-sm text-text-muted leading-relaxed">
                            Generate a snarky narrative covering the weekly awards, power
                            rankings, luck index, and standings — all in one voice.
                        </p>
                        <button
                            onClick={() => generate(false)}
                            className="flex items-center gap-2 px-4 py-2 bg-mustard text-forest font-sans font-bold text-sm rounded-lg hover:bg-yellow-400 transition-colors shadow-mustard"
                        >
                            <Sparkles className="w-3.5 h-3.5" />
                            Generate Weekly Report
                        </button>
                    </div>
                )}

                {/* Loading — progressive step feedback */}
                {loading && (
                    <div className="flex flex-col gap-2 py-1">
                        {STEPS.map(({ key, label }) => {
                            const isDone = doneSteps.includes(key);
                            const isActive = currentStep === key;
                            const isPending = !isDone && !isActive;
                            return (
                                <div
                                    key={key}
                                    className={`flex items-center gap-2 text-sm transition-colors ${isDone ? "text-text-muted"
                                        : isActive ? "text-ds-text"
                                            : "text-border"
                                        }`}
                                >
                                    {isDone ? (
                                        <CheckCircle2 className="w-4 h-4 text-mustard shrink-0" />
                                    ) : isActive ? (
                                        <RefreshCw className="w-4 h-4 animate-spin text-mustard shrink-0" />
                                    ) : (
                                        <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                                            <span className="w-1.5 h-1.5 rounded-full bg-border" />
                                        </span>
                                    )}
                                    <span className={isPending ? "opacity-40" : ""}>{label}</span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Error */}
                {error && !loading && (
                    <div className="flex flex-col gap-2">
                        <p className="text-sm text-red-500">{error}</p>
                        <button
                            onClick={() => generate(true)}
                            className="flex items-center gap-1.5 text-xs text-text-muted hover:text-ds-text transition-colors w-fit"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Try again
                        </button>
                    </div>
                )}

                {/* Sections */}
                {sections && !loading && (
                    <div>
                        <div className="flex flex-col divide-y divide-border-card">
                            {REPORT_SECTIONS.map(({ key, emoji, label }) =>
                                sections[key] ? (
                                    <div key={key} className="py-3 first:pt-0 last:pb-0">
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <span className="text-sm leading-none">{emoji}</span>
                                            <span className="text-xs font-sans font-bold uppercase tracking-wider text-text-muted">
                                                {label}
                                            </span>
                                        </div>
                                        <p className="text-sm text-ds-text leading-relaxed font-sans">
                                            {sections[key]}
                                        </p>
                                    </div>
                                ) : null
                            )}
                        </div>

                        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border-card">
                            <button
                                onClick={handleCopy}
                                className="flex items-center gap-1.5 text-xs text-text-muted hover:text-ds-text transition-colors"
                            >
                                {copied ? (
                                    <Check className="w-3 h-3 text-green-500" />
                                ) : (
                                    <Copy className="w-3 h-3" />
                                )}
                                {copied ? "Copied!" : "Copy for group chat"}
                            </button>
                            <button
                                onClick={() => generate(true)}
                                disabled={loading}
                                className="flex items-center gap-1.5 text-xs text-text-muted hover:text-ds-text transition-colors disabled:opacity-40"
                            >
                                <RefreshCw className="w-3 h-3" />
                                Regenerate
                            </button>
                            <span className="text-text-muted text-xs ml-auto">
                                Powered by Gemini
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WeeklyNarrative;
